import Spinner from '@/components/dataDisplay/Spinner';
import theme from '@/constants/theme';
import React, { PropsWithChildren, ButtonHTMLAttributes, ElementType } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline' | 'main';
type ButtonSize = 'small' | 'medium' | 'large';

const styles: Record<ButtonVariant, React.CSSProperties> = {
  main: {
    backgroundColor: theme.colors.primary,
    color: theme.colors.text.contrast,
    fontWeight: 'bold',
    justifyContent: 'center',
  },
  primary: {
    backgroundColor: theme.colors.primary,
    color: theme.colors.text.contrast,
  },
  secondary: {
    backgroundColor: theme.colors.secondary,
    color: theme.colors.text.default,
  },
  danger: {
    backgroundColor: theme.colors.danger,
    color: theme.colors.text.default,
  },
  ghost: {
    backgroundColor: 'transparent',
    color: theme.colors.primary,
  },
  outline: {
    backgroundColor: 'transparent',
    color: theme.colors.primary,
    border: `1px solid ${theme.colors.primary}`,
  },
};

const sizes: Record<ButtonSize, React.CSSProperties> = {
  small: {
    padding: '4px 8px',
    height: 40,
    fontSize: 14,
    borderRadius: 8,
  },
  medium: {
    padding: '10px 16px',
    height: 48,
    fontSize: 16,
  },
  large: {
    padding: '12px 16px',
    height: 56,
    fontSize: 18,
  },
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  Icon?: ElementType;
  className?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  sx?: React.CSSProperties;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  ({ Icon, children, className, loading, variant = 'primary', size = 'medium', sx, ...props }, ref) => {
    return (
      <button
        style={{
          borderRadius: 8,
          padding: '10px 16px',
          height: 48,
          fontSize: 16,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          border: 'none',
          fontFamily: 'inherit',
          ...sizes[size],
          ...styles[variant],
          ...props.style,
          ...sx,
        }}
        ref={ref}
        {...props}
      >
        {loading && <Spinner color={styles[variant].color} width={22} style={{ marginRight: 4 }} />}
        {Icon && <Icon size={size === 'small' ? 8 : 20} />}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';

export default Button;
